/**
 * Rejections that are noise rather than defects. Asked as one question so the Sentry
 * `beforeSend` filter and the global unhandled-rejection toast cannot drift apart —
 * an event worth hiding from one is worth hiding from the other.
 */

/**
 * True for a rejection that is only a cancelled request — an `AbortController.abort()`
 * rejecting whatever was still in flight.
 *
 * Nothing in this app aborts a request whose result still matters. Every
 * `AbortController` here supersedes a request the user has already moved past
 * (`Robots.vm.ts` and `useLiveRobotStreaming.ts`, both of which already swallow the
 * AbortError at their own call site), and MapLibre aborts a map tile's fetch the instant
 * the tile scrolls out of view (`TileManager._removeTile` -> `abortTile`). That last one
 * has no call site of ours to catch it, so it surfaced at
 * `window.onunhandledrejection` — where an ordinary map pan toasted the operator, popped
 * Sentry's crash-report dialog and filed PORTAL-WEB-X7. An abort nobody awaited is not
 * actionable by construction: whoever aborted it is the one who stopped caring about the
 * answer.
 *
 * Deliberately keyed on `name`, not on the message: "signal is aborted without reason" is
 * the browser's wording and a browser update may change it, while `AbortError` is spec.
 */
export function isAbortRejection(reason: unknown): boolean {
	return typeof reason === "object" && reason !== null && (reason as { name?: unknown }).name === "AbortError";
}

/**
 * True for an error the axios response interceptor has already dealt with end to end —
 * it decided the user-facing message, decided whether Sentry should hear about it, and
 * marked the error with `_interceptorHandled` on its way back out (see `api/client.ts`).
 *
 * The session-expiry 401 is the case that matters. The interceptor clears the tokens,
 * logs the user out, toasts "Session expired. Please log in again." and deliberately does
 * *not* report — an expired token is the system working, not a defect. It still rejects
 * the promise so call sites can unwind, and a call site with no `catch` lets that
 * rejection reach `window.onunhandledrejection`, where Sentry's own global handler files
 * it as an unhandled error anyway and `beforeSend` pops the crash-report dialog on top of
 * the toast (PORTAL-WEB-XC: a tab left open for three days, on the Submit Jobs form).
 *
 * So the interceptor's "don't report this" only holds while every call site catches. Asked
 * here instead, it holds by construction — the same reasoning that moved the MapLibre
 * abort into `isAbortRejection` above.
 *
 * Keyed on the flag the interceptor sets, not on the status code, so the answer stays
 * wherever the handling decision was actually made. `beforeSend` pairs this with a
 * `mechanism.handled` check, since the interceptor's *deliberate* reports (network
 * failure, 403, 5xx) are captured explicitly with `handled: true` and must survive.
 */
export function isInterceptorHandled(reason: unknown): boolean {
	return (
		typeof reason === "object" &&
		reason !== null &&
		(reason as { _interceptorHandled?: unknown })._interceptorHandled === true
	);
}
